import { FormControl, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import InputFieldContainer from "./InputFieldContainer";
import StandalonePriceListSelect from "./StandalonePriceListSelect";
import { gql, useQuery } from "@apollo/client";
import { GET_FABRIC_PRICES } from "@/components/queries/additionalComponentQueries";
import { useEffect, useState } from "react";

const FABRIC_PRICE_CATEGORIES = gql`
query FindManyFabricPriceCategory ($manufacturerId: Int) {
  findManyFabricPriceCategory (where:  {
     manufacturerId:  {
        equals: $manufacturerId
     }
  }) {
    id
    group_number
    manufacturerId
  }
}`;

interface FabricCategoryPrice {
  id?: number;
  price: number;
  price_listId: number;
  fabrice_price_category: {
    group_number: number;
    id: number;
  };
}

interface FabricCategoryPricesProps {
  control: any;
  name: string;
  additionalComponentId: number;
  advancedProductId: number;
  manufacturerId: number;
}

export default function FabricCategoryPrices({
  control,
  name,
  additionalComponentId,
  advancedProductId,
  manufacturerId,
}: FabricCategoryPricesProps) {
  const [selectedPriceListId, setSelectedPriceListId] = useState<number | null>(
    null
  );

  const { data: categoriesData } = useQuery(FABRIC_PRICE_CATEGORIES, {
    variables: { manufacturerId },
  });
  const { data, loading, refetch } = useQuery(GET_FABRIC_PRICES, {
    variables: {
      where: { id: additionalComponentId },
      advancedProductId,
      priceListId: selectedPriceListId || undefined,
    },
    skip: !additionalComponentId || !advancedProductId,
  });

  useEffect(() => {
    if (selectedPriceListId && additionalComponentId && advancedProductId) {
      refetch({
        where: { id: additionalComponentId },
        advancedProductId,
        priceListId: selectedPriceListId,
      });
    }
  }, [selectedPriceListId, additionalComponentId, advancedProductId, refetch]);

  if (loading || !categoriesData) {
    return <div>Loading...</div>;
  }

  const updatePricesFromQuery = (field: any) => {
    // Get all possible categories
    const allCategories = categoriesData.findManyFabricPriceCategory;
    // Get existing prices
    const existingPrices =
      data?.findUniqueAdditionalComponent
        ?.additional_component_to_advanced_product?.[0]
        ?.price_fabric_category || [];

    // Create a map of existing prices by category id
    const priceMap = new Map(
      existingPrices.map((price: FabricCategoryPrice) => [
        price.fabrice_price_category.group_number,
        price,
      ])
    );

    // Create array with all categories, using existing prices where available
    const allPrices = allCategories
      .map((category: any) => {
        const existingPrice = priceMap.get(category.id);
        return (
          existingPrice || {
            price: 0,
            price_listId: selectedPriceListId,
            fabrice_price_category: {
              group_number: category.group_number,
              id: category.id,
            },
          }
        );
      })
      .sort(
        (a, b) =>
          a.fabrice_price_category.group_number -
          b.fabrice_price_category.group_number
      );

    field.onChange(allPrices);
  };
  return (
    <InputFieldContainer
      label="Extra Price by Fabric Category"
      control={control}
      name={name}
      render={({ field: pricesField }) => {
        useEffect(() => {
          updatePricesFromQuery(pricesField);
        }, [data]);

        return (
          <FormItem className="col-span-3">
            <Card>
              <div className="flex gap-4">
                <div className="w-1/3 p-4">
                  <StandalonePriceListSelect
                    unSelect={false}
                    value={selectedPriceListId}
                    onPriceListChange={setSelectedPriceListId}
                  />
                </div>
                <CardContent className="w-2/3">
                  <div className="space-y-2 pt-4">
                    {pricesField.value?.map(
                      (price: FabricCategoryPrice, index: number) => (
                        <div
                          key={price.id || index}
                          className="flex justify-between items-right  rounded-lg"
                        >
                          <span className="text-sm font-medium pt-2">
                            CAT {price.fabrice_price_category.group_number}
                          </span>
                          <Input
                            type="number"
                            disabled
                            value={price.price}
                            onChange={(e) => {
                              const newPrices = [...pricesField.value];
                              newPrices[index].price = parseFloat(
                                e.target.value
                              );
                              pricesField.onChange(newPrices);
                            }}
                            className="w-24"
                          />
                        </div>
                      )
                    )}
                  </div>
                </CardContent>
              </div>
            </Card>

            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
